import {Badge} from '@orbit-ui/components/badge';
import {Button} from '@orbit-ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@orbit-ui/components/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@orbit-ui/components/dialog';
import {Input} from '@orbit-ui/components/input';
import {Label} from '@orbit-ui/components/label';
import {PageHeader} from '@orbit-ui/components/page-header';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@orbit-ui/components/table';
import {
  PlusIcon,
  SearchIcon,
  TrashIcon,
  EditIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  ServerIcon,
} from 'lucide-react';
import * as React from 'react';

// Sample registry data
const registries = [
  {
    id: 'reg-1',
    name: 'Organization OCI Registry',
    url: 'registry.cosmonic.com',
    type: 'OCI',
    status: 'Healthy',
    components: 127,
    added: '2023-10-15',
    lastSync: '2025-03-31T10:24:53Z',
  },
  {
    id: 'reg-2',
    name: 'Development Registry',
    url: 'dev-registry.internal.net',
    type: 'OCI',
    status: 'Healthy',
    components: 43,
    added: '2023-11-03',
    lastSync: '2025-03-31T08:15:22Z',
  },
  {
    id: 'reg-3',
    name: 'Legacy Artifact Registry',
    url: 'artifacts.cosmonic.com',
    type: 'Generic',
    status: 'Issues',
    components: 51,
    added: '2023-08-22',
    lastSync: '2025-03-30T16:45:11Z',
  },
];

export const AdminRegistriesView = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);

  // Filter registries based on search term
  const filteredRegistries = registries.filter(
    (registry) =>
      registry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      registry.url.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Dialog form state
  const [formData, setFormData] = React.useState({
    name: '',
    url: '',
    type: 'OCI',
    username: '',
    password: '',
  });

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;
    setFormData((previous) => ({...previous, [name]: value}));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    console.log('Registry form submitted:', formData);
    // In a real app, this would add the registry to the backend
    setIsAddDialogOpen(false);
    setFormData({
      name: '',
      url: '',
      type: 'OCI',
      username: '',
      password: '',
    });
  };

  return (
    <div className="container mx-auto space-y-6 py-6">
      <PageHeader
        title="Registry Management"
        description="Connect to OCI and other registries for component artifacts"
        action={
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <PlusIcon className="mr-2 size-4" />
                Add Registry
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-screen-xs">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>Add Registry Connection</DialogTitle>
                  <DialogDescription>
                    Connect to an external registry where component artifacts are stored.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      className="col-span-3"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="url" className="text-right">
                      URL
                    </Label>
                    <Input
                      id="url"
                      name="url"
                      value={formData.url}
                      onChange={handleFormChange}
                      className="col-span-3"
                      placeholder="registry.example.com"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="type" className="text-right">
                      Type
                    </Label>
                    <Input
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleFormChange}
                      className="col-span-3"
                      placeholder="OCI"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="username" className="text-right">
                      Username
                    </Label>
                    <Input
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleFormChange}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="password" className="text-right">
                      Password
                    </Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleFormChange}
                      className="col-span-3"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit">Add Registry</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Search bar */}
      <div className="flex w-full max-w-sm items-center space-x-2">
        <div className="relative w-full">
          <SearchIcon className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search registries..."
            className="w-full pl-8"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
      </div>

      {/* Registry list */}
      <Card>
        <CardHeader>
          <CardTitle>Connected Registries</CardTitle>
          <CardDescription>External registry connections for component artifacts</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Components</TableHead>
                <TableHead>Last Sync</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRegistries.map((registry) => (
                <TableRow key={registry.id}>
                  <TableCell className="font-medium">{registry.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <ServerIcon className="mr-2 size-4 text-muted-foreground" />
                      {registry.url}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{registry.type}</Badge>
                  </TableCell>
                  <TableCell>
                    {registry.status === 'Healthy' ? (
                      <div className="flex items-center">
                        <CheckCircleIcon className="mr-1 size-4 text-green-500" />
                        <span>Healthy</span>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <AlertCircleIcon className="mr-1 size-4 text-amber-500" />
                        <span>Issues</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{registry.components}</TableCell>
                  <TableCell>{new Date(registry.lastSync).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon">
                      <EditIcon className="size-4" />
                      <span className="sr-only">Edit</span>
                    </Button>
                    <Button variant="ghost" size="icon">
                      <TrashIcon className="size-4" />
                      <span className="sr-only">Delete</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredRegistries.length === 0 && (
            <div className="py-6 text-center text-muted-foreground">
              No registries match your search criteria
            </div>
          )}
        </CardContent>
        <CardFooter className="flex-wrap justify-between">
          <div>
            <p className="text-sm text-muted-foreground">
              {registries.length} registries connected
            </p>
          </div>
          <Button variant="outline" size="sm">
            Test Connections
          </Button>
        </CardFooter>
      </Card>

      {/* Registry health overview */}
      <Card>
        <CardHeader>
          <CardTitle>Registry Health</CardTitle>
          <CardDescription>Health metrics for connected registries</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="mb-2 text-sm font-medium">Successful Pulls (24h)</div>
              <div className="text-2xl font-bold">1,247</div>
              <div className="mt-1 text-xs text-muted-foreground">+12% from previous day</div>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="mb-2 text-sm font-medium">Failed Pulls (24h)</div>
              <div className="text-2xl font-bold">3</div>
              <div className="mt-1 text-xs text-muted-foreground">-5% from previous day</div>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="mb-2 text-sm font-medium">Avg Response Time</div>
              <div className="text-2xl font-bold">142ms</div>
              <div className="mt-1 text-xs text-muted-foreground">-8ms from previous day</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
